import { vec2 } from 'gl-matrix';
import type { ReadonlyMat2d, ReadonlyVec2 } from 'gl-matrix';

import { boxCreate, boxExpandToFitPoint } from '@canvas/math/box';
import type { Box, ReadonlyBox } from '@canvas/math/box';
import { boxToRegion } from '@canvas/math/box/convert';
import { intersectionPointOfTwoLines } from '@canvas/math/intersection-points';
import { lerp } from '@canvas/math/numbers';
import { regionIntersectsLine } from '@canvas/math/spatial/region-search';

import { type ReadonlyCubicBezier, type CubicBezier } from './types.ts';

/**
 * Creates a cubic bezier object.
 * All coordinates are absolute.
 *
 * @param start vec2 of start of the bezier
 * @param control1 vec2 of the first control point
 * @param control2 vec2 of the second control point
 * @param end vec2 of end of the bezier
 * @returns CubicBezier
 */
export function createCubicBezier(
  start: vec2,
  control1: vec2,
  control2: vec2,
  end: vec2,
): CubicBezier {
  return {
    start,
    control1,
    control2,
    end,
  };
}

export function transformCubicBezier(
  out: CubicBezier,
  bezier: ReadonlyCubicBezier,
  transform: ReadonlyMat2d,
): CubicBezier {
  vec2.transformMat2d(out.start, bezier.start, transform);
  vec2.transformMat2d(out.control1, bezier.control1, transform);
  vec2.transformMat2d(out.control2, bezier.control2, transform);
  vec2.transformMat2d(out.end, bezier.end, transform);
  return out;
}

/**
 * Creates a cubic bezier object from raw number values
 * internally creating 4 vec2 instances.
 * All coordinates are absolute.
 *
 * @param sx start x position
 * @param sy start y position
 * @param cp1x control point 1 x position
 * @param cp1y control point 1 y position
 * @param cp2x control point 2 x position
 * @param cp2y control point 2 y position
 * @param ex end x position
 * @param ey end y position
 * @returns CubicBezier
 */
export function createCubicBezierFromValues(
  sx: number,
  sy: number,
  cp1x: number,
  cp1y: number,
  cp2x: number,
  cp2y: number,
  ex: number,
  ey: number,
): CubicBezier {
  return {
    start: vec2.fromValues(sx, sy),
    control1: vec2.fromValues(cp1x, cp1y),
    control2: vec2.fromValues(cp2x, cp2y),
    end: vec2.fromValues(ex, ey),
  };
}

/**
 * Gets a point on a cubic bezier
 * where t = 0 is the start point and t = 1 is the end point of the bezier
 * and values between lie along the bezier curve.
 *
 * Adapted from https://github.com/Pomax/bezierjs/blob/master/src/bezier.js
 *
 * @param out the vec2 that will contain the output
 * @param bezier the bezier
 * @param t the proportion along the bezier from 0 (start) to 1 (end)
 * @returns out
 */
export function getPointOnCubicBezier(
  out: vec2,
  bezier: ReadonlyCubicBezier,
  t: number,
): vec2 {
  if (t === 0) {
    return vec2.copy(out, bezier.start);
  }

  if (t === 1) {
    return vec2.copy(out, bezier.end);
  }

  const t2 = t * t;
  const mt = 1 - t;
  const mt2 = mt * mt;

  const a = mt2 * mt;
  const b = mt2 * t * 3;
  const c = mt * t2 * 3;
  const d = t * t2;

  const [x0, y0] = bezier.start;
  const [x1, y1] = bezier.control1;
  const [x2, y2] = bezier.control2;
  const [x3, y3] = bezier.end;

  out[0] = a * x0 + b * x1 + c * x2 + d * x3;
  out[1] = a * y0 + b * y1 + c * y2 + d * y3;
  return out;
}

/**
 * Gets the tangent (slope of the curve) on a cubic bezier in radians
 * where t = 0 is the start point and t = 1 is the end point of the bezier
 * and values between lie along the bezier curve.
 *
 * @param bezier the bezier
 * @param t the proportion along the bezier from 0 (start) to 1 (end)
 * @returns the tangent in radians
 */
export function getTangentOnCubicBezier(bezier: ReadonlyCubicBezier, t: number): number {
  const [x0, y0] = bezier.start;
  const [x1, y1] = bezier.control1;
  const [x2, y2] = bezier.control2;
  const [x3, y3] = bezier.end;

  // calculate the derivative of the cubic Bezier curve at parameter t
  // find complement of t
  const ct = 1 - t;
  const dx = 3 * ct * ct * (x1 - x0) + 6 * ct * t * (x2 - x1) + 3 * t * t * (x3 - x2);
  const dy = 3 * ct * ct * (y1 - y0) + 6 * ct * t * (y2 - y1) + 3 * t * t * (y3 - y2);

  // calculate the angle of the tangent using atan2
  return Math.atan2(dy, dx);
}

/**
 * Calculates a series of points along a cubic bezier, calling the `each` callback for each one.
 *
 * @param bezier the input bezier to walk along
 * @param amount the number of points to calculate
 * e.g. if amount is 3 then `each` will be called three times
 * once for the start, middle and end of the bezier
 * @param each the callback to call for each point calculated
 * If `false` is optionally returned then iteration is cancelled immediately
 * Note that the point (vec2) that is passed into each() is a flyweight,
 * and should be cloned if you need to persist its values elsewhere
 * @param startProportion (optional) the proportion along the bezier to start the walk, between 0 and 1,
 * where 0.5 is halfway along the bezier
 * @param endProportion (optional) the proportion along the bezier to end the walk, between 0 and 1
 * where 0.5 is halfway along the bezier
 */
export function walkPointsAlongCubicBezier(
  bezier: ReadonlyCubicBezier,
  amount: number,
  each: (p1: ReadonlyVec2, t: number, i: number) => boolean | void,
  startProportion = 0,
  endProportion = 1,
): void {
  const p = vec2.create();
  for (let i = 0; i < amount; i++) {
    const t = lerp(startProportion, endProportion, i / (amount - 1));
    getPointOnCubicBezier(p, bezier, t);
    if (each(p, t, i) === false) {
      return;
    }
  }
}

// higher values increase length calcualtion accuracy but take longer to run
// tweak this as required
const DEFAULT_BEZIER_LENGTH_RESOLUTION = 32;

/**
 * Returns the length of a cubic bezier.
 * This is a fast approximation, breaking the bezier into a series of lines
 * and summing their lengths.
 *
 * @param bezier the bezier to check
 * @param quality how many lines to break this bezier into. Must be larger than 0
 * @returns the length (number)
 */
export function getCubicBezierLength(
  bezier: ReadonlyCubicBezier,
  resolution = DEFAULT_BEZIER_LENGTH_RESOLUTION,
): number {
  let length = 0;
  let prev: vec2 | undefined;
  const diff = vec2.create();

  walkPointsAlongCubicBezier(bezier, resolution, (point: ReadonlyVec2) => {
    if (prev) {
      vec2.sub(diff, point, prev);
      length += vec2.len(diff);
      vec2.copy(prev, point);
    } else {
      prev = vec2.clone(point);
    }
  });

  return length;
}

/**
 * Given a cubic bezier and a target point, this finds the closest point
 * on the bezier to the target point.
 *
 * The proportion describes how far along the bezier the closest point is.
 * The distance describes how far the target point is from that closest point.
 *
 * This method is popular due to its simplicity.
 * It samples a set of points along the bezier, finds the closest point,
 * then samples a much smaller set of points around the closest point to get a more accurate approximation.
 *
 * Adapted from the method used in https://github.com/Pomax/bezierjs/blob/master/src/bezier.js
 * Alternative methods described here: https://stackoverflow.com/questions/2742610/closest-point-on-a-cubic-bezier-curve
 *
 * @param bezier the bezier to check
 * @param targetPoint the target point
 * @param resolution1 the number of points to check along the bezier curve in the first pass (prefer powers of 2)
 * @param resolution2 the number of points to check around the closest point in the second pass (prefer powers of 2)
 * @returns an object containing the point (vec2), proportion (number), and distance (number)
 */
export function getClosestPointAlongCubicBezier(
  bezier: ReadonlyCubicBezier,
  targetPoint: ReadonlyVec2,
  resolution1 = 64,
  resolution2 = 8,
): { point: vec2; proportion: number; distance: number } {
  const diff = vec2.create();

  // step 1: walk over a series of subdivided points on the bezier curve to find the closest one
  const closestPoint = vec2.create();
  let closestSqrDist = Number.POSITIVE_INFINITY;
  let closestProportion = -1;

  walkPointsAlongCubicBezier(
    bezier,
    resolution1 + 1, // add one so that a power of 2 resolution will test a centred proportion (proportion = 0.5)
    (point: ReadonlyVec2, proportion: number) => {
      vec2.sub(diff, targetPoint, point);
      const sqrDist = vec2.sqrLen(diff);
      if (sqrDist < closestSqrDist) {
        vec2.copy(closestPoint, point);
        closestSqrDist = sqrDist;
        closestProportion = proportion;
      }
    },
  );

  // step 2: walk over a much smaller range spanning slightly either side of the closest point
  // and return a new closest point, to narrow down a more accurate approximation
  const divisionLength = 1 / resolution1;
  const startProportion = Math.max(0, closestProportion - divisionLength);
  const endProportion = Math.min(1, closestProportion + divisionLength);

  closestSqrDist = Number.POSITIVE_INFINITY;
  closestProportion = -1;

  walkPointsAlongCubicBezier(
    bezier,
    resolution2 + 1, // add one so that a power of 2 resolution will test a centred proportion
    (point: ReadonlyVec2, proportion: number) => {
      vec2.sub(diff, targetPoint, point);
      const sqrDist = vec2.sqrLen(diff);
      if (sqrDist < closestSqrDist) {
        vec2.copy(closestPoint, point);
        closestSqrDist = sqrDist;
        closestProportion = proportion;
      }
    },
    startProportion,
    endProportion,
  );

  // TODO: consider getting the closest point along a line between the two closest points
  // to improve accuracy when the bezier curve's sampling points are spaced very wide in comparison
  // to how close the target point is

  return {
    point: closestPoint,
    proportion: closestProportion,
    distance: Math.sqrt(closestSqrDist),
  };
}

/**
 * Given a cubic bezier, a target point and a distance,
 * this assesses if the target point is within the distance to the bezier curve.
 *
 * THIS FUNCTION IS NOT OPTIMISED!
 *
 * Consider stopping iteration as soon as any point is within distance
 * and consider using a series of isPointWithinDistanceOfLine() function calls
 * (the current method will not work well if the distance to check is smaller than 1/512
 * the length of the bezier curve, as the target point could fall between sampled bezier points)
 *
 * @param bezier the bezier to check
 * @param targetPoint the target point
 * @param distance the distance
 * @returns boolean
 */
export function isPointWithinDistanceOfCubicBezier(
  bezier: ReadonlyCubicBezier,
  targetPoint: ReadonlyVec2,
  distance: number,
  params?: {
    resolution1?: number;
    resolution2?: number;
  },
): boolean {
  const result = getClosestPointAlongCubicBezier(
    bezier,
    targetPoint,
    params?.resolution1,
    params?.resolution2,
  );
  return result.distance <= distance;
}

// higher values increase bounding box calculation accuracy but take longer to run
// tweak this as required
const CUBIC_BEZIER_BOUNDING_BOX_RESOLUTION = 128;

/**
 * Given a bezier curve, this returns its bounding box.
 *
 * THIS FUNCTION IS NOT OPTIMISED!
 *
 * Consider using the bounding box method used by https://github.com/Pomax/bezierjs/blob/4e767299184ab4079dc8d7c970020103db14f431/src/bezier.js#L503
 * calculating the extrema of the curve, given that the smallest and largest x/y values on a bezier curve
 * lie on either end point or at curve inflection points on the x and y axes
 *
 *
 * @param bezier the bezier curve to find the bounding box of
 * @param result an optional initial bounding box to mutate
 * @returns box the bounding box
 */
export function getCubicBezierBoundingBox(
  bezier: ReadonlyCubicBezier,
  result?: Box,
): Box {
  const [sx, sy] = bezier.start;
  const box = result ?? boxCreate(sx, sx, sy, sy);

  walkPointsAlongCubicBezier(
    bezier,
    CUBIC_BEZIER_BOUNDING_BOX_RESOLUTION,
    (point: ReadonlyVec2) => {
      boxExpandToFitPoint(box, point);
    },
  );

  return box;
}

// higher values increase intersection accuracy on sharper corners but take longer to run
// tweak this as required
const BOX_INTERSECTS_CUBIC_BEZIER_RESOLUTION = 32;

/**
 * Given a bezier curve and a box to intersect,
 * this returns if the curve is intersecting the box.
 *
 * @param bezier the bezier curve to intersect with a box
 * @param box box the box to intersect with a bezier curve
 * @returns boolean true if any part of the bezier curve is contained within the box, false otherwise
 */
export function boxIntersectsCubicBezier(
  bezier: ReadonlyCubicBezier,
  box: ReadonlyBox,
): boolean {
  const region = boxToRegion(box);
  let prev: vec2 | undefined;
  let intersects = false;

  walkPointsAlongCubicBezier(
    bezier,
    BOX_INTERSECTS_CUBIC_BEZIER_RESOLUTION,
    (point: ReadonlyVec2) => {
      if (prev) {
        if (regionIntersectsLine(region, prev, point)) {
          intersects = true;
          return false;
        }
        vec2.copy(prev, point);
      } else {
        prev = vec2.clone(point);
      }
    },
  );

  return intersects;
}

export type IntersectionPointOfLineAndBezierResult = {
  // intersection point
  point: vec2;
  // proportion along line that intersection occurs, value from 0 - 1
  proportionAlongLine: number;
};

/**
 * Returns details of each intersection point of a line and a cubic bezier
 * or an empty list if there are no intersections
 *
 * @param bezier The cubic bezier
 * @param lineStart The start of the line
 * @param lineEnd The end of the line
 * @param resolution The number of points to sample from the bezier, dividing it into `resolution - 1` lines.
 * Higher values increase intersection accuracy on sharper corners but take longer to run.
 *
 * @returns A list of intersection points and how far proportionally along the line they are
 */
export function intersectionPointsOfLineAndBezier(
  bezier: ReadonlyCubicBezier,
  lineStart: ReadonlyVec2,
  lineEnd: ReadonlyVec2,
  resolution: number,
): IntersectionPointOfLineAndBezierResult[] {
  let prev: vec2 | undefined;
  const intersections: IntersectionPointOfLineAndBezierResult[] = [];

  walkPointsAlongCubicBezier(bezier, resolution, (point: ReadonlyVec2) => {
    if (prev) {
      const intersection = intersectionPointOfTwoLines(lineStart, lineEnd, prev, point);
      if (intersection !== undefined) {
        intersections.push({
          point: intersection.point,
          // We only put the proportion along the straight line intersecting
          // the bezier that the intersection occurs; the proportion along the
          // bezier is non-trivial to calculate as the t-parameter used for
          // walking points does not result in evenly-spaced points.
          proportionAlongLine: intersection.proportionAlongA,
        });
      }
      vec2.copy(prev, point);
    } else {
      prev = vec2.clone(point);
    }
  });

  return intersections;
}

/**
 * Fast approximation of the bounding box of a cubic bezier. This bounding box is guaranteed to contain the curve
 * but is not guaranteed (or even likely) to be the tightest bounding box for the bezier.
 *
 * This is useful for hot path code where it's useful to cheaply exclude an entity from a more expensive intersection check
 *
 * Calculates the maximum and minimum x and y coordinates of the start, end and control points to create a rough bounding box.
 *
 * @param bezier curve to approximate the bounding box of
 * @param result the box to apply the result to, creates a new box if not defined
 * @returns approximate bounding box for the curve that's guaranteed to contain it, just not tightly
 */
export const approximateCubicBezierBoundingBox = (
  bezier: ReadonlyCubicBezier,
  result: Box = boxCreate(),
): Box => {
  result.left = Math.min(
    bezier.start[0],
    bezier.control1[0],
    bezier.control2[0],
    bezier.end[0],
  );
  result.right = Math.max(
    bezier.start[0],
    bezier.control1[0],
    bezier.control2[0],
    bezier.end[0],
  );
  result.top = Math.min(
    bezier.start[1],
    bezier.control1[1],
    bezier.control2[1],
    bezier.end[1],
  );
  result.bottom = Math.max(
    bezier.start[1],
    bezier.control1[1],
    bezier.control2[1],
    bezier.end[1],
  );
  return result;
};
